import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import {
  api,
  type WorkspaceStats,
  type TraceEvent,
  type DashboardStatsResponse,
  type AnomalyBaselineReport,
  type AnomalyAlert,
  type EventsQuery,
  type TrustScoreResult,
  type ShieldScoreBreakdown,
} from '../api/client';
import { PipelineStrip, type PipelineStep } from '../components/PipelineStrip';
import {
  buildStepsFromEvents, deriveOverallStatus, formatToolName,
  formatDuration, getStatusStyle,
} from '../utils/trace-utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  Cell,
} from 'recharts';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Lightning,
  Clock,
  Users,
  HourglassHigh,
  CurrencyDollar,
  Warning,
} from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MetricRow {
  label: string;
  value: string | number;
  bar?: number; // 0-1 for mini bar
  color?: string;
  icon: React.ElementType;
  badge?: 'red' | 'amber';
}

interface GroupedActivity {
  task_id: string;
  displayName: string;
  actor_id: string;
  timestamp: string;
  events: TraceEvent[];
  status: string;
  durationMs: number;
  steps: PipelineStep[];
  toolCallCount: number;
}

function buildMetrics(dashStats: DashboardStatsResponse | null, basicStats: WorkspaceStats | null): MetricRow[] {
  const m = dashStats?.metrics;
  const rpm = m?.requests_per_minute ?? 0;
  const blocked = m?.blocked_24h ?? 0;
  const latency = m?.avg_latency_ms ?? 0;
  const agents = m?.active_agents ?? basicStats?.members ?? 0;
  const pending = m?.pending_approvals ?? 0;
  const burn = m?.budget_burn_percent ?? 0;

  return [
    {
      label: 'Enforced / min',
      value: rpm.toFixed(1),
      bar: Math.min(rpm / 100, 1),
      icon: Lightning,
    },
    {
      label: 'Blocked today',
      value: blocked,
      color: blocked > 0 ? 'var(--color-error)' : undefined,
      badge: blocked > 0 ? 'red' : undefined,
      icon: ShieldCheck,
    },
    {
      label: 'Avg latency',
      value: `${latency} ms`,
      bar: Math.min(latency / 300, 1),
      icon: Clock,
    },
    {
      label: 'Active agents',
      value: agents,
      icon: Users,
    },
    {
      label: 'Pending approvals',
      value: pending,
      badge: pending > 0 ? 'amber' : undefined,
      icon: HourglassHigh,
    },
    {
      label: 'Budget burn',
      value: `${burn}%`,
      bar: Math.min(burn / 100, 1),
      icon: CurrencyDollar,
    },
  ];
}

const STAGE_LABELS: Record<string, string> = {
  auth: 'Auth',
  policy: 'Policy',
  dlp: 'DLP Scan',
  budget: 'Budget',
  execute: 'Execute',
};

const RADAR_AXIS_LABELS: Record<string, string> = {
  request_rate: 'Request Rate',
  error_rate: 'Error Rate',
  latency: 'Latency',
  new_tools: 'New Tools',
  capability_escalations: 'Cap. Escalation',
  off_hours: 'Off Hours',
};

function buildWaterfallData(dashStats: DashboardStatsResponse | null, recentEvents?: TraceEvent[]) {
  if (dashStats?.pipeline_throughput?.length) {
    return dashStats.pipeline_throughput.map(s => ({
      step: STAGE_LABELS[s.stage] || s.stage,
      passed: s.passed,
      failed: s.failed,
    }));
  }

  // Fallback: derive throughput from recent_events when dashStats is unavailable
  if (recentEvents && recentEvents.length > 0) {
    const counts: Record<string, number> = { auth: 0, policy: 0, dlp: 0, budget: 0, execute: 0 };
    for (const e of recentEvents) {
      const t = (e.event_type || '').toUpperCase();
      if (t === 'TOOL_CALL_RECEIVED') counts.auth++;
      else if (t === 'POLICY_DECIDED') counts.policy++;
      else if (t === 'DLP_SCANNED') counts.dlp++;
      else if (t === 'BUDGET_CHECKED') counts.budget++;
      else if (t === 'TOOL_EXECUTED') counts.execute++;
    }
    if (Object.values(counts).every(v => v === 0)) {
      counts.auth = recentEvents.length;
      counts.policy = recentEvents.length;
      counts.dlp = recentEvents.length;
      counts.budget = recentEvents.length;
      counts.execute = recentEvents.length;
    }
    return [
      { step: 'Auth', passed: counts.auth, failed: 0 },
      { step: 'Policy', passed: counts.policy, failed: 0 },
      { step: 'DLP Scan', passed: counts.dlp, failed: 0 },
      { step: 'Budget', passed: counts.budget, failed: 0 },
      { step: 'Execute', passed: counts.execute, failed: 0 },
    ];
  }

  return [
    { step: 'Auth', passed: 0, failed: 0 },
    { step: 'Policy', passed: 0, failed: 0 },
    { step: 'DLP Scan', passed: 0, failed: 0 },
    { step: 'Budget', passed: 0, failed: 0 },
    { step: 'Execute', passed: 0, failed: 0 },
  ];
}

function buildRadarData(anomalyReport: AnomalyBaselineReport | null) {
  const axes = ['request_rate', 'error_rate', 'latency', 'new_tools', 'capability_escalations', 'off_hours'];
  return axes.map(axis => ({
    axis: RADAR_AXIS_LABELS[axis] || axis,
    current: anomalyReport?.current?.[axis] ?? 0,
    baseline: anomalyReport?.baseline?.[axis] ?? 0,
  }));
}

// ---------------------------------------------------------------------------
// Group events into activities (by tool_call_id / task_id)
// ---------------------------------------------------------------------------

function groupEventsIntoActivities(events: TraceEvent[]): GroupedActivity[] {
  // Group by task_id so all tool calls within a task become one row
  const groups = new Map<string, TraceEvent[]>();

  for (const e of events) {
    const key = e.task_id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(e);
  }

  const activities: GroupedActivity[] = [];
  for (const [taskId, groupEvents] of groups) {
    // Sort events chronologically within group
    groupEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    const first = groupEvents[0];
    const last = groupEvents[groupEvents.length - 1];
    const durationMs = new Date(last.timestamp).getTime() - new Date(first.timestamp).getTime();

    // Count unique tool calls in this task
    const toolCallIds = new Set(groupEvents.map(e => e.tool_call_id).filter(Boolean));
    const toolCallCount = toolCallIds.size || 1;

    // Build display name: show primary tool + count if multiple calls
    const uniqueTools = new Set(groupEvents.map(e => e.tool_name).filter(Boolean));
    let displayName: string;
    if (uniqueTools.size === 1) {
      displayName = formatToolName([...uniqueTools][0]!, groupEvents);
      if (toolCallCount > 1) displayName += ` (×${toolCallCount})`;
    } else if (uniqueTools.size > 1) {
      displayName = `${toolCallCount} tool calls`;
    } else {
      displayName = taskId;
    }

    activities.push({
      task_id: taskId,
      displayName,
      actor_id: first.actor_id || '',
      timestamp: first.timestamp,
      events: groupEvents,
      status: deriveOverallStatus(groupEvents),
      durationMs,
      steps: buildStepsFromEvents(groupEvents),
      toolCallCount,
    });
  }

  // Sort by latest timestamp first
  activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return activities;
}

// ---------------------------------------------------------------------------
// Shield Score Gauge (SVG arc)
// ---------------------------------------------------------------------------

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function ShieldScoreGauge({ score, breakdown, total }: { score: number; breakdown?: ShieldScoreBreakdown; total?: number }) {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const r = 72;
  const strokeWidth = 12;

  const b = breakdown || { allowed_percent: 100, transformed_percent: 0, approval_percent: 0, blocked_percent: 0 };
  const segments = [
    { pct: b.allowed_percent / 100, color: 'var(--accent-teal)' },
    { pct: b.transformed_percent / 100, color: 'var(--accent-violet)' },
    { pct: b.approval_percent / 100, color: 'var(--color-warning)' },
    { pct: b.blocked_percent / 100, color: 'var(--color-error)' },
  ].filter(s => s.pct > 0);

  const totalAngle = 270; // degrees of arc
  const startAngle = 135; // start from bottom-left

  function polarToCartesian(angleDeg: number) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(startDeg: number, endDeg: number) {
    const start = polarToCartesian(startDeg);
    const end = polarToCartesian(endDeg);
    const sweep = endDeg - startDeg;
    const largeArc = sweep > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  }

  let accAngle = startAngle;
  const arcs = segments.map((seg, i) => {
    const segAngle = seg.pct * totalAngle;
    const s = accAngle;
    const e = accAngle + segAngle;
    accAngle = e;
    return (
      <path
        key={i}
        d={arcPath(s, e)}
        fill="none"
        stroke={seg.color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={0.9}
      />
    );
  });

  // Background arc
  const bgArc = arcPath(startAngle, startAngle + totalAngle);

  const statusText = score >= 95 ? 'All systems nominal' : score >= 80 ? 'Minor anomalies' : 'Attention needed';

  // Raw counts
  const hasRawCounts = breakdown?.allowed_count !== undefined;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <path
          d={bgArc}
          fill="none"
          stroke="var(--border)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.4}
        />
        {arcs}
        {/* Center text */}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--text-primary)"
          fontSize="32"
          fontWeight="700"
          fontFamily="var(--font-sans)"
        >
          {score.toFixed(1)}%
        </text>
        <text
          x={cx}
          y={cy + 22}
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--accent-teal)"
          fontSize="10"
          fontWeight="600"
          fontFamily="var(--font-sans)"
          letterSpacing="0.12em"
        >
          PROTECTED
        </text>
      </svg>
      <p className="mt-1 text-xs" style={{ color: 'var(--text-tertiary)' }}>
        {statusText}
      </p>
      {hasRawCounts && (
        <p
          className="mt-2 text-[10px] tabular-nums text-center"
          style={{ color: 'var(--text-tertiary)' }}
        >
          {formatCount(breakdown!.allowed_count!)} allowed
          {(breakdown!.transformed_count ?? 0) > 0 && <> &middot; {formatCount(breakdown!.transformed_count!)} transformed</>}
          {(breakdown!.approval_count ?? 0) > 0 && <> &middot; {formatCount(breakdown!.approval_count!)} approval</>}
          {(breakdown!.blocked_count ?? 0) > 0 && <> &middot; {formatCount(breakdown!.blocked_count!)} blocked</>}
          {total !== undefined && <> &middot; {formatCount(total)} total</>}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Key Metrics Panel
// ---------------------------------------------------------------------------

function KeyMetricsPanel({ metrics }: { metrics: MetricRow[] }) {
  return (
    <div className="flex flex-col gap-3">
      {metrics.map((m) => (
        <div key={m.label} className="flex items-center gap-3">
          <m.icon
            size={18}
            weight="duotone"
            style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
          />
          <span
            className="text-xs w-28 shrink-0"
            style={{ color: 'var(--text-secondary)' }}
          >
            {m.label}
          </span>
          <div className="flex-1 flex items-center gap-2">
            {m.bar !== undefined && (
              <div
                className="flex-1 h-1.5 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${m.bar * 100}%`,
                    background: m.color || 'var(--gradient-accent)',
                  }}
                />
              </div>
            )}
            <span
              className="text-sm font-semibold tabular-nums"
              style={{ color: m.color || 'var(--text-primary)' }}
            >
              {m.value}
            </span>
            {m.badge === 'red' && (
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: 'var(--color-error)' }}
              />
            )}
            {m.badge === 'amber' && (
              <span
                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium shrink-0"
                style={{
                  backgroundColor: 'var(--color-warning-muted)',
                  color: 'var(--color-warning)',
                }}
              >
                pending
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom Recharts Tooltip (dark theme)
// ---------------------------------------------------------------------------

function WaterfallTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const passed = payload.find((p: any) => p.dataKey === 'passed')?.value ?? 0;
  const failed = payload.find((p: any) => p.dataKey === 'failed')?.value ?? 0;
  return (
    <div
      className="rounded-md px-3 py-2 text-xs"
      style={{
        backgroundColor: 'var(--surface-3)',
        border: '1px solid var(--border)',
        color: 'var(--text-primary)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <div style={{ color: 'var(--text-secondary)' }}>{label}</div>
      <div className="font-semibold mt-0.5" style={{ color: 'var(--accent-teal)' }}>
        {passed} passed
      </div>
      {failed > 0 && (
        <div className="font-semibold mt-0.5" style={{ color: 'var(--color-error)' }}>
          {failed} failed
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline Throughput Waterfall (stacked: passed + failed)
// ---------------------------------------------------------------------------

const WATERFALL_COLORS = [
  '#06B6A4',
  '#1ba3a0',
  '#3a91aa',
  '#5580b5',
  '#6e70bf',
  '#8160c9',
  '#8B5CF6',
];

function PipelineWaterfall({ data }: { data: { step: string; passed: number; failed: number }[] }) {
  const maxVal = Math.max(...data.map(d => d.passed + d.failed), 1);
  const hasFailed = data.some(d => d.failed > 0);

  return (
    <div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 20, bottom: 4, left: 4 }}
          barCategoryGap="20%"
        >
          <XAxis
            type="number"
            hide
            domain={[0, Math.ceil(maxVal * 1.2) || 50]}
          />
          <YAxis
            type="category"
            dataKey="step"
            width={70}
            tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip content={<WaterfallTooltip />} cursor={false} />
          <Bar dataKey="passed" stackId="a" radius={hasFailed ? undefined : [0, 4, 4, 0]} maxBarSize={18}>
            {data.map((_, i) => (
              <Cell key={i} fill={WATERFALL_COLORS[i % WATERFALL_COLORS.length]} />
            ))}
          </Bar>
          {hasFailed && (
            <Bar dataKey="failed" stackId="a" radius={[0, 4, 4, 0]} maxBarSize={18} fill="var(--color-error)" opacity={0.7} />
          )}
        </BarChart>
      </ResponsiveContainer>
      <div
        className="flex items-center justify-center gap-4 mt-1 text-[10px]"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: '#06B6A4' }} />
          Passed
        </span>
        {hasFailed && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--color-error)', opacity: 0.7 }} />
            Failed
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Anomaly Alerts Banner
// ---------------------------------------------------------------------------

const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };
const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: 'var(--color-error-muted)', text: 'var(--color-error)' },
  medium: { bg: 'var(--color-warning-muted)', text: 'var(--color-warning)' },
  low: { bg: 'var(--color-info-muted)', text: 'var(--color-info)' },
};

function AnomalyAlertsBanner({ alerts }: { alerts: AnomalyAlert[] }) {
  if (!alerts || alerts.length === 0) return null;

  const sorted = [...alerts].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3));
  const shown = sorted.slice(0, 3);
  const overflow = sorted.length - shown.length;

  return (
    <div className="mb-3 space-y-1.5">
      {shown.map((alert) => {
        const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.low;
        return (
          <div
            key={alert.alert_id}
            className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11px]"
            style={{ backgroundColor: colors.bg }}
          >
            <Warning size={14} weight="fill" style={{ color: colors.text, flexShrink: 0 }} />
            <span style={{ color: colors.text }} className="font-medium">
              {alert.anomaly_type.replace(/_/g, ' ')}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>
              {alert.entity_type}:{alert.entity_id.slice(0, 12)}
            </span>
            <span className="ml-auto tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
              {alert.current_value.toFixed(1)} vs {alert.baseline_mean.toFixed(1)} baseline
            </span>
          </div>
        );
      })}
      {overflow > 0 && (
        <Link to="/anomalies" className="block text-[10px] pl-1 hover:underline" style={{ color: 'var(--text-tertiary)' }}>
          +{overflow} more alert{overflow > 1 ? 's' : ''} →
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Anomaly Radar
// ---------------------------------------------------------------------------

function AnomalyRadar({ data }: { data: { axis: string; current: number; baseline: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <RadarChart
        data={data}
        cx="50%"
        cy="50%"
        outerRadius="70%"
      >
        <PolarGrid
          stroke="var(--border)"
          strokeOpacity={0.5}
        />
        <PolarAngleAxis
          dataKey="axis"
          tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
        />
        <Radar
          name="Baseline"
          dataKey="baseline"
          stroke="var(--text-tertiary)"
          fill="var(--text-tertiary)"
          fillOpacity={0.1}
          strokeWidth={1}
        />
        <Radar
          name="Current"
          dataKey="current"
          stroke="var(--accent-teal)"
          fill="var(--accent-teal)"
          fillOpacity={0.2}
          strokeWidth={1.5}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Trust Leaderboard Card
// ---------------------------------------------------------------------------

const RISK_COLORS: Record<string, { bg: string; text: string }> = {
  low: { bg: 'var(--color-teal-muted)', text: 'var(--accent-teal)' },
  medium: { bg: 'var(--color-warning-muted)', text: 'var(--color-warning)' },
  high: { bg: 'var(--color-error-muted)', text: 'var(--color-error)' },
  critical: { bg: 'var(--color-error-muted)', text: 'var(--color-error)' },
};

function topImpactFactor(breakdown: TrustScoreResult['breakdown']): string {
  const factors: [string, number][] = [
    ['anomalies', breakdown.anomalies.impact],
    ['dlp', breakdown.dlp.impact],
    ['errors', breakdown.error_rate.impact],
    ['escalations', breakdown.escalations.impact],
    ['budget', breakdown.budget_velocity.impact],
  ];
  factors.sort((a, b) => b[1] - a[1]);
  return factors[0][1] > 0 ? factors[0][0] : 'none';
}

function TrustLeaderboardCard({ agents }: { agents: TrustScoreResult[] }) {
  const sorted = [...agents].sort((a, b) => a.score - b.score).slice(0, 5);

  return (
    <div className="flex flex-col gap-2.5">
      {sorted.map((agent) => {
        const colors = RISK_COLORS[agent.risk_level] || RISK_COLORS.low;
        const factor = topImpactFactor(agent.breakdown);
        return (
          <div key={agent.actor_id} className="flex items-center gap-3">
            {/* Score circle */}
            <div
              className="flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold shrink-0"
              style={{ backgroundColor: colors.bg, color: colors.text }}
            >
              {agent.score}
            </div>
            {/* Actor ID */}
            <div className="flex-1 min-w-0">
              <div
                className="text-xs font-medium truncate"
                style={{ color: 'var(--text-primary)' }}
              >
                {agent.actor_id}
              </div>
              {factor !== 'none' && (
                <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  top factor: {factor}
                </div>
              )}
            </div>
            {/* Risk badge */}
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0"
              style={{ backgroundColor: colors.bg, color: colors.text }}
            >
              {agent.risk_level}
            </span>
            {/* Mini score bar */}
            <div
              className="w-16 h-1.5 rounded-full overflow-hidden shrink-0"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${agent.score}%`,
                  backgroundColor: colors.text,
                  opacity: 0.7,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card wrapper
// ---------------------------------------------------------------------------

function Card({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`rounded-xl border p-6 ${className}`}
      style={{
        backgroundColor: 'var(--surface-1)',
        borderColor: 'var(--border)',
      }}
    >
      {children}
    </motion.div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="text-[11px] font-medium uppercase tracking-wider mb-4"
      style={{ color: 'var(--text-tertiary)' }}
    >
      {children}
    </h3>
  );
}

// ---------------------------------------------------------------------------
// Dashboard (main export)
// ---------------------------------------------------------------------------

export function Dashboard() {
  const { currentWorkspaceId: workspaceId } = useAuth();
  const [stats, setStats] = useState<WorkspaceStats | null>(null);
  const [dashStats, setDashStats] = useState<DashboardStatsResponse | null>(null);
  const [anomalyReport, setAnomalyReport] = useState<AnomalyBaselineReport | null>(null);
  const [trustLeaderboard, setTrustLeaderboard] = useState<TrustScoreResult[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    async function loadAll() {
      try {
        const [basicStats, dashboard, anomaly, trust] = await Promise.all([
          api.getWorkspaceStats(workspaceId!),
          api.getDashboardStats(workspaceId!).catch(() => null),
          api.getAnomalyBaseline(workspaceId!).catch(() => null),
          api.getTrustLeaderboard(workspaceId!).catch(() => null),
        ]);
        setStats(basicStats);
        setDashStats(dashboard);
        setAnomalyReport(anomaly);
        setTrustLeaderboard(trust?.agents ?? null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load dashboard data.',
        );
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, [workspaceId]);

  const metrics = useMemo(() => buildMetrics(dashStats, stats), [dashStats, stats]);
  const waterfallData = useMemo(() => buildWaterfallData(dashStats, stats?.recent_events), [dashStats, stats]);
  const radarData = useMemo(() => buildRadarData(anomalyReport), [anomalyReport]);

  const navigate = useNavigate();
  const shieldScore = dashStats?.shield_score?.score ?? 100;
  const shieldBreakdown = dashStats?.shield_score?.breakdown;
  const shieldTotal = dashStats?.shield_score?.total;

  // --- Loading ---
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner-gradient h-8 w-8" />
      </div>
    );
  }

  // --- No workspace ---
  if (!workspaceId) {
    return (
      <div
        className="rounded-xl border p-8 text-center"
        style={{
          backgroundColor: 'var(--surface-1)',
          borderColor: 'var(--border)',
        }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          No workspace found
        </h2>
        <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Complete onboarding to create your first workspace.
        </p>
        <Link
          to="/onboarding"
          className="mt-4 inline-block rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ background: 'var(--gradient-accent)' }}
        >
          Start Onboarding
        </Link>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div
        className="rounded-lg border px-4 py-3 text-sm"
        style={{
          backgroundColor: 'var(--color-error-muted)',
          borderColor: 'var(--color-error)',
          color: 'var(--color-error)',
        }}
      >
        {error}
      </div>
    );
  }

  // --- Main Dashboard ---
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Mission Control
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Enforcement posture and pipeline activity
        </p>
      </div>

      {/* Row 1: Shield Score + Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Shield Score */}
        <Card className="flex items-center justify-center">
          <div className="w-full">
            <SectionTitle>Shield Score</SectionTitle>
            <ShieldScoreGauge score={shieldScore} breakdown={shieldBreakdown} total={shieldTotal} />
            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-4 text-[10px]">
              {[
                { label: 'Allowed', color: 'var(--accent-teal)' },
                { label: 'Transformed', color: 'var(--accent-violet)' },
                { label: 'Approval', color: 'var(--color-warning)' },
                { label: 'Blocked', color: 'var(--color-error)' },
              ].map((l) => (
                <span key={l.label} className="flex items-center gap-1">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: l.color }}
                  />
                  <span style={{ color: 'var(--text-tertiary)' }}>{l.label}</span>
                </span>
              ))}
            </div>
          </div>
        </Card>

        {/* Key Metrics */}
        <Card>
          <SectionTitle>Key Metrics</SectionTitle>
          <KeyMetricsPanel metrics={metrics} />
        </Card>
      </div>

      {/* Row 2: Waterfall + Anomaly Radar */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pipeline Throughput Waterfall */}
        <Card>
          <SectionTitle>Pipeline Throughput</SectionTitle>
          <PipelineWaterfall data={waterfallData} />
        </Card>

        {/* Anomaly Radar */}
        <Card>
          <SectionTitle>Anomaly Radar</SectionTitle>
          <AnomalyAlertsBanner alerts={anomalyReport?.alerts ?? []} />
          <AnomalyRadar data={radarData} />
          <div
            className="flex items-center justify-center gap-4 mt-1 text-[10px]"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: 'var(--accent-teal)', opacity: 0.6 }}
              />
              Current
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: 'var(--text-tertiary)', opacity: 0.4 }}
              />
              Baseline
            </span>
          </div>
        </Card>
      </div>

      {/* Row 3: Trust Leaderboard (only when data available) */}
      {trustLeaderboard && trustLeaderboard.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <SectionTitle>Agent Trust Leaderboard</SectionTitle>
            <TrustLeaderboardCard agents={trustLeaderboard} />
          </Card>
          <div /> {/* placeholder for future widget */}
        </div>
      )}

      {/* Row 4: Live Activity */}
      <LiveActivityTable workspaceId={workspaceId} navigate={navigate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter chip
// ---------------------------------------------------------------------------

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border"
      style={{
        backgroundColor: active ? 'var(--accent-teal)' : 'transparent',
        color: active ? 'var(--surface-0)' : 'var(--text-secondary)',
        borderColor: active ? 'var(--accent-teal)' : 'var(--border)',
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Live Activity Table (grouped by tool_call_id)
// ---------------------------------------------------------------------------

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Allowed', value: 'ok' },
  { label: 'Blocked', value: 'denied,rate_limited,budget_exceeded' },
  { label: 'Approval', value: 'approval' },
  { label: 'Error', value: 'error' },
  { label: 'Incident', value: 'incident' },
];

function LiveActivityTable({ workspaceId, navigate }: { workspaceId: string; navigate: (path: string) => void }) {
  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [live, setLive] = useState(true);
  const prevTaskIdsRef = useRef<Set<string>>(new Set());
  const [newTaskIds, setNewTaskIds] = useState<Set<string>>(new Set());

  const fetchEvents = useCallback(async () => {
    try {
      const query: EventsQuery = {
        sort: 'desc',
        limit: 150,
      };
      if (searchQuery) query.q = searchQuery;

      const data = await api.getEvents(workspaceId, query);
      setEvents(data.events);

      // Detect new task IDs for highlight
      const tcIds = new Set<string>();
      for (const e of data.events) {
        tcIds.add(e.task_id);
      }
      const fresh = new Set<string>();
      for (const id of tcIds) {
        if (!prevTaskIdsRef.current.has(id)) fresh.add(id);
      }
      if (prevTaskIdsRef.current.size > 0 && fresh.size > 0) {
        setNewTaskIds(fresh);
        setTimeout(() => setNewTaskIds(new Set()), 2000);
      }
      prevTaskIdsRef.current = tcIds;
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [workspaceId, searchQuery]);

  // Initial load + polling
  useEffect(() => {
    fetchEvents();
    if (!live) return;
    const interval = setInterval(fetchEvents, 3000);
    return () => clearInterval(interval);
  }, [fetchEvents, live]);

  // Group events into activities
  const activities = useMemo(() => groupEventsIntoActivities(events), [events]);

  // Apply status filter post-grouping
  const filteredActivities = useMemo(() => {
    if (!statusFilter) return activities;
    const filterValues = statusFilter.split(',');
    return activities.filter(a => filterValues.some(v => a.status === v));
  }, [activities, statusFilter]);

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <SectionTitle>Live Activity</SectionTitle>
          {live && (
            <span className="relative flex h-2 w-2 -mt-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-teal opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-teal" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
            {filteredActivities.length} tasks
          </span>
          <button
            onClick={() => setLive(l => !l)}
            className="rounded-md px-2 py-1 text-[11px] font-medium border transition-colors"
            style={{
              backgroundColor: live ? 'var(--color-teal-muted)' : 'transparent',
              color: live ? 'var(--accent-teal)' : 'var(--text-tertiary)',
              borderColor: live ? 'var(--accent-teal)' : 'var(--border)',
            }}
          >
            {live ? 'Live' : 'Paused'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {STATUS_FILTERS.map(f => (
          <FilterChip
            key={f.value}
            label={f.label}
            active={statusFilter === f.value}
            onClick={() => setStatusFilter(f.value)}
          />
        ))}
        <div className="ml-auto">
          <input
            type="text"
            placeholder="Search tool, actor, task..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="rounded-md border px-2.5 py-1.5 text-xs w-48 outline-none transition-colors focus:border-[var(--accent-teal)]"
            style={{
              backgroundColor: 'var(--surface-0)',
              borderColor: 'var(--border)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* Table */}
      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner-gradient h-6 w-6" />
        </div>
      ) : filteredActivities.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
          {statusFilter || searchQuery
            ? 'No tasks match the current filters.'
            : 'No recent activity. Make your first tool call to see events here.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr
                className="border-b"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--surface-2)' }}
              >
                {['Tool', 'Pipeline', 'Status', 'Duration', 'Time'].map(h => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredActivities.map((activity) => {
                const isNew = newTaskIds.has(activity.task_id);
                const statusStyle = getStatusStyle(activity.status);
                return (
                  <tr
                    key={activity.task_id}
                    className="border-b last:border-0 transition-all cursor-pointer"
                    style={{
                      borderColor: 'var(--border-subtle)',
                      backgroundColor: isNew ? 'rgba(6, 182, 164, 0.06)' : undefined,
                    }}
                    onClick={() => navigate(`/traces/${activity.task_id}`)}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--surface-2)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = isNew ? 'rgba(6, 182, 164, 0.06)' : 'transparent'; }}
                  >
                    <td className="px-4 py-2.5">
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {activity.displayName}
                      </div>
                      <div className="font-mono text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        {activity.task_id}
                        {activity.toolCallCount > 1 && ` · ${activity.events.length} events`}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <PipelineStrip steps={activity.steps} size="sm" />
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                        style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                      >
                        {statusStyle.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                      {activity.durationMs > 0 ? formatDuration(activity.durationMs) : '\u2014'}
                    </td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {timeAgo(activity.timestamp)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 5_000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ts).toLocaleDateString();
}
